import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useContext, useMemo, useEffect, useRef } from 'react';
import { Search, CnIcon, CnStatusTag, CnTooltip, Menu } from '@alife/cn-ui';
import { useMemoizedFn, useDebounceFn, useLatest, useUpdateEffect, useMap, } from 'ahooks';
import classnames from 'classnames';
import $i18n from 'panda-i18n';
import { event, store } from '@ice/stark-data';
import { genSearchTags, isFilterValueEmpty, reportHottag } from '../utils';
import { maxSearchTagsLengthShowLimitInLine, hottagPrefix } from '../const';
import SearchTag from './search-tag';
import QuickFieldButton from './quick-field-button';
import CnOneSearchContext from '../context';
import '../index.scss';
function renderAnalysisTag(type) {
    if (!type) {
        return;
    }
    const name = {
        FILTER: $i18n.get({ id: 'Filtration', dm: '过滤' }),
        METRIC: $i18n.get({ id: 'Indicators', dm: '指标' }),
        DIMENSION: $i18n.get({ id: '39445465682546688.CNTM', dm: '维度' }),
    }[type];
    const status = {
        FILTER: 'info',
        METRIC: 'success',
        DIMENSION: 'warning',
    }[type];
    return name ? (_jsx(CnStatusTag, { size: 'small', status: status, children: name })) : null;
}
function SearchInput({ searchTags = [], onChangeSearchTags, onSearch, keyword, onChangeSearchWord, onFetchSuggest, onFocus, onClearClick, customShowLimitNum, placeholder = '', disabled = false, quickSearchFields = [], fieldRender, quickSearchButtonIcon, isDisabledSpaceKeyPress = false, isSuggestPopupViewTargetInputSearch, isEnhancedAnalisys = false, isFireSearchWhenDisableEnd = true, loading = false, isSelectSuggestAutoSearch = false, isAutoExpand = false, customFilterSlot, }) {
    // 从store中获取插槽
    const leftSlotPlugin = store.get('cn-one-search-suggest-view-left-slot');
    const rightSlotPlugin = store.get('cn-one-search-suggest-view-right-slot');
    const customSuggestItemSlotPlugin = store.get('cn-one-search-suggest-custom-item-slot');
    const suggestHeadSlotPlugin = store.get('cn-one-search-suggest-view-head-slot');
    const LeftSlotPluginComp = leftSlotPlugin?.component;
    const RightSlotPluginComp = rightSlotPlugin?.component;
    const SuggestHeadSlotPluginComp = suggestHeadSlotPlugin?.component;
    const SuggestCustomItemComp = customSuggestItemSlotPlugin?.component || null;
    const realShowLimitNum = customShowLimitNum || maxSearchTagsLengthShowLimitInLine;
    const [suggest, setSuggest] = useState([]);
    const [suggestRecommend, suggestHistory] = useMemo(() => {
        return [
            suggest.filter((item) => !item.history),
            suggest.filter((item) => item.history),
        ];
    }, [suggest]);
    const [suggestVisible, setSuggestVisible] = useState(false);
    const [, { set: storeSuggestList, get: getSuggestList }] = useMap([]);
    const [loadingSuggest, setLoadingSuggest] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const isSearchingRef = useLatest(isSearching);
    const [isComposition, setIsComposition] = useState(false);
    // 是否需要展开展示所有的tag
    const [needExpendShowSearchTags, setNeedExpendShowSearchTags] = useState(false);
    // 当前键盘操作选中的tag index
    const [currentTagIndex, setCurrentTagIndex] = useState(-1);
    const [currentSuggestIndex, setCurrentSuggestIndex] = useState(-1);
    const [isSugRecommendFolder, setIsSugRecommendFolder] = useState(false);
    const [isSugHisFolder, setIsSugHisFolder] = useState(false);
    const { containerRef } = useContext(CnOneSearchContext);
    const searchRef = useRef(null);
    const { run: handleKeywordChange, flush: fetchSuggestImmediately } = useDebounceFn((value) => {
        const enhanceValue = value.trim();
        const invokeSuggest = (newSuggestList) => {
            if (value === keyword) {
                setSuggest(newSuggestList);
                setSuggestVisible(newSuggestList.length > 0);
            }
        };
        const storeKey = enhanceValue || 'defaultSuggest';
        const cacheSuggestList = getSuggestList(storeKey);
        if (cacheSuggestList) {
            invokeSuggest(cacheSuggestList);
        }
        else {
            setLoadingSuggest(true);
            onFetchSuggest(enhanceValue)
                .then((newSuggestList) => {
                invokeSuggest(newSuggestList);
                storeSuggestList(storeKey, newSuggestList);
            })
                .finally(() => {
                setLoadingSuggest(false);
            });
        }
    }, {
        wait: 50,
    });
    const handleKeywordSearch = useMemoizedFn((newKeyword) => {
        setIsSearching(true);
        setNeedExpendShowSearchTags(isAutoExpand || false);
        onSearch(newKeyword);
        // 无法获取是否还在loading，暂定延迟200ms解除searching状态
        setTimeout(() => {
            if (isSearching) {
                setIsSearching(false);
            }
        }, 200);
    });
    useUpdateEffect(() => {
        if (!disabled) {
            if (isFireSearchWhenDisableEnd) {
                onSearch(keyword);
            }
        }
        else {
            setNeedExpendShowSearchTags(isAutoExpand || false);
        }
    }, [disabled, isFireSearchWhenDisableEnd]);
    // 键盘事件响应
    const handleKeyDownEvent = useMemoizedFn((e) => {
        const keyCodeName = e.code?.toLocaleLowerCase();
        const isSpaceKeyPress = keyCodeName === 'space' || e.keyCode === 32;
        const isDeleteKeyPress = ['backspace', 'delete'].includes(keyCodeName) ||
            [8, 46].includes(e.keyCode);
        const isEnterKeyPress = keyCodeName === 'enter' || e.keyCode === 13;
        const isLeftKeyPress = keyCodeName === 'left' || e.keyCode === 37;
        const isRightKeyPress = keyCodeName === 'right' || e.keyCode === 39;
        const isUpKeyPress = keyCodeName === 'up' || e.keyCode === 38;
        const isDownKeyPress = keyCodeName === 'down' || e.keyCode === 40;
        const hasMoreTags = searchTags.length > realShowLimitNum && !needExpendShowSearchTags;
        const setTagIndex = (index) => {
            const nextTag = searchTags[index];
            if (index < 0 || (nextTag && !nextTag.isFromView)) {
                setCurrentTagIndex(index);
            }
        };
        // 上下键
        if ((isUpKeyPress || isDownKeyPress) && suggestVisible) {
            let targetCurrentSuggestIndex;
            if (isDownKeyPress) {
                if (currentSuggestIndex === -1 ||
                    currentSuggestIndex ===
                        (isSugHisFolder
                            ? suggest.length - suggestHistory?.length - 1
                            : suggest.length - 1)) {
                    targetCurrentSuggestIndex = 0;
                }
                else if (currentSuggestIndex < suggest.length - 1) {
                    targetCurrentSuggestIndex = currentSuggestIndex + 1;
                }
            }
            if (isUpKeyPress) {
                if (currentSuggestIndex === -1 ||
                    currentSuggestIndex ===
                        (isSugRecommendFolder ? suggestRecommend?.length || 0 : 0)) {
                    targetCurrentSuggestIndex = suggest.length - 1;
                }
                else if (currentSuggestIndex > 0) {
                    targetCurrentSuggestIndex = currentSuggestIndex - 1;
                }
            }
            if (typeof targetCurrentSuggestIndex !== 'undefined') {
                if (isSugRecommendFolder &&
                    suggestRecommend?.length &&
                    targetCurrentSuggestIndex < suggestRecommend?.length) {
                    targetCurrentSuggestIndex =
                        targetCurrentSuggestIndex + suggestRecommend?.length || 0;
                }
                if (isSugHisFolder &&
                    suggestHistory?.length &&
                    targetCurrentSuggestIndex > suggestHistory?.length) {
                    targetCurrentSuggestIndex =
                        targetCurrentSuggestIndex - suggestHistory?.length || 0;
                }
                setCurrentSuggestIndex(targetCurrentSuggestIndex);
            }
            e.preventDefault();
        }
        // 空格键
        if (isSpaceKeyPress &&
            !isDisabledSpaceKeyPress &&
            !isComposition &&
            keyword.trim()) {
            onChangeSearchTags(genSearchTags(searchTags, {
                keyword,
            }));
            onChangeSearchWord('');
            e.preventDefault();
        }
        // 删除键
        if (isDeleteKeyPress && keyword.trim().length === 0) {
            onChangeSearchWord('');
            // 已经有选中的tag，并且选中的tag不是省略数量显示的tag，则删除选中的tag
            if (currentTagIndex > -1) {
                if (!hasMoreTags || currentTagIndex !== realShowLimitNum) {
                    const newSearchTags = [...searchTags];
                    // 触发全局删除search tag事件，与tag close同理，兼容外部逻辑处理
                    const deleteTag = newSearchTags.find((_, delIndex) => delIndex === currentTagIndex);
                    deleteTag && handleTagClose(deleteTag);
                    newSearchTags.splice(currentTagIndex, 1);
                    // 删除后重置为不选中tag的状态
                    setCurrentTagIndex(-1);
                    onChangeSearchTags(newSearchTags);
                    reportHottag({
                        hottag: `${hottagPrefix}.searchTag.keyboardDelete`,
                        name: '搜索标签-键盘删除',
                    });
                }
            }
            else if (needExpendShowSearchTags || !hasMoreTags) {
                // 展开状态下，选中最后的一个tag
                setTagIndex(searchTags.length - 1);
            }
            else {
                // 否则选中省略数量显示的tag
                setTagIndex(realShowLimitNum);
            }
        }
        // 左右键
        if ((isLeftKeyPress || isRightKeyPress) && keyword.trim().length === 0) {
            const maxTagsLength = !hasMoreTags
                ? searchTags.length
                : realShowLimitNum + 1;
            if (isLeftKeyPress) {
                if (currentTagIndex > 0) {
                    setTagIndex(currentTagIndex - 1);
                }
                else if (currentTagIndex === 0) {
                    setTagIndex(-1);
                }
                else {
                    setTagIndex(maxTagsLength - 1);
                }
            }
            else if (isRightKeyPress && currentTagIndex > -1) {
                // 右键应该只在当前有选中项时才能起作用
                if (currentTagIndex < maxTagsLength - 1) {
                    setTagIndex(currentTagIndex + 1);
                }
                else {
                    setTagIndex(-1);
                }
            }
        }
        // 回车键
        if (isEnterKeyPress) {
            // 当前选中的更多按钮tag，则展开
            if (hasMoreTags && currentTagIndex === realShowLimitNum) {
                setNeedExpendShowSearchTags(true);
                setTagIndex(-1);
                reportHottag({
                    hottag: `${hottagPrefix}.searchTag.keyboardExpend`,
                    name: '搜索标签-键盘回车展开更多',
                });
            }
            else if (currentTagIndex > -1) {
                // 当前选中的tag，则尝试变编辑态
                handleTagClick(currentTagIndex);
                setTagIndex(-1);
                reportHottag({
                    hottag: `${hottagPrefix}.searchTag.keyboardSelected`,
                    name: '搜索标签-键盘回车改为编辑态',
                });
            }
            else if (currentSuggestIndex > -1 &&
                currentSuggestIndex < suggest.length &&
                suggest.length > 0) {
                // 在输入框选中后回车，则直接选中
                let suggestItem = null;
                if (suggestRecommend && suggestRecommend.length) {
                    if (currentSuggestIndex < suggestRecommend.length) {
                        suggestItem = suggestRecommend[currentSuggestIndex];
                    }
                    else if (suggestHistory &&
                        suggestHistory.length &&
                        currentSuggestIndex >= suggestRecommend?.length) {
                        suggestItem =
                            suggestHistory[currentSuggestIndex - suggestRecommend?.length];
                    }
                }
                else if (suggestHistory && suggestHistory.length) {
                    suggestItem = suggestHistory[currentSuggestIndex];
                }
                if (suggestItem) {
                    handleSuggestItemSelected(suggestItem);
                }
                setCurrentSuggestIndex(-1);
            }
        }
    });
    // tag click
    const handleTagClick = useMemoizedFn((tagIndex) => {
        if (tagIndex > -1 && tagIndex < searchTags.length) {
            const tagItem = searchTags[tagIndex];
            // 普通的keyword允许点击后重新修改
            if (!tagItem.name && Array.isArray(tagItem.values)) {
                onChangeSearchWord(tagItem.values[0]);
                onChangeSearchTags(genSearchTags(searchTags.filter((item, index) => index !== tagIndex), {
                    keyword,
                }));
                containerRef?.current?.querySelector('input')?.focus();
            }
        }
    });
    // 处理搜索下拉项选中
    const handleSuggestItemSelected = (suggestItem = { value: '' }) => {
        const { tagEnabled, value, name, sugType, content, entityType } = suggestItem;
        setSuggestVisible(false);
        if (tagEnabled) {
            onChangeSearchWord('');
            onChangeSearchTags(genSearchTags(searchTags, {
                suggestItem: {
                    ...suggestItem,
                    isStrictCompare: true,
                    // 当搜索点选的时间类型标签值不为空的情况下采取覆盖值原则
                    isRepeatCover: value
                        ? value.indexOf('suggest-fake-value') < 0 &&
                            (entityType === 'RangePickerField' ||
                                entityType === 'RangeDatePickerField')
                        : false,
                },
            }));
            setNeedExpendShowSearchTags(true);
        }
        else if (sugType === 'custom') {
            try {
                const savedSearchTags = JSON.parse(content || '{}').searchTags || [];
                onChangeSearchWord('');
                onChangeSearchTags(genSearchTags(savedSearchTags, { keyword: '' }));
                setTimeout(() => {
                    onSearch('');
                }, 0);
            }
            catch (error) {
                console.log(error);
            }
            fetchSuggestImmediately();
            return;
        }
        else {
            const searchValue = (value || name || '').trim();
            if (isSelectSuggestAutoSearch) {
                onChangeSearchWord('');
                setTimeout(() => {
                    onSearch(searchValue);
                }, 0);
                return;
            }
            onChangeSearchWord(searchValue);
            handleKeywordChange(searchValue);
            fetchSuggestImmediately();
        }
        containerRef?.current?.querySelector('input')?.focus();
    };
    // 点击Tag的关闭按钮
    const handleTagClose = useMemoizedFn((tag) => {
        event.emit('cnOneSearchTagCloseClick', tag);
    });
    // 点击清空按钮
    const handleClearClick = useMemoizedFn((e) => {
        e.stopPropagation();
        e.preventDefault();
        onClearClick && onClearClick();
    });
    // 是否是键盘操作选择搜索提示而触发的onSearch
    let isOnSearchTriggerFromEnterSelectSearchItem = false;
    const suggestPopupProps = {};
    const inputEle = containerRef?.current
        ? containerRef.current.querySelector('input')
        : null;
    if (inputEle && isSuggestPopupViewTargetInputSearch) {
        suggestPopupProps.popupProps = {
            target: inputEle,
        };
        const customWidth = (rightSlotPlugin?.width || 0) + (leftSlotPlugin?.width || 0) + 600;
        suggestPopupProps.popupStyle = {
            width: `${inputEle.offsetWidth < 400
                ? 402
                : Math.min(inputEle.offsetWidth, customWidth)}px`,
        };
    }
    const renderSuggestItems = (item, index) => {
        const { history = false, value = '', name = '', sugType, analyzeType = '', } = item;
        const key = `${item.value}${analyzeType}${index}`;
        const isValueEmpty = isFilterValueEmpty(value) || name === value;
        const handleItemClick = (e) => {
            e.stopPropagation();
            handleSuggestItemSelected(item);
        };
        let iconType = history ? 'time' : 'search';
        if (sugType === 'custom') {
            iconType = 'icon-user-list';
        }
        return (_jsx(Menu.Item, { className: currentSuggestIndex === index ? 'next-focus' : '', children: _jsx("div", { className: 'cn-one-search-suggest-item', onClick: handleItemClick, children: sugType !== 'custom' || SuggestCustomItemComp === null ? (_jsxs(_Fragment, { children: [isEnhancedAnalisys ? (renderAnalysisTag(analyzeType)) : (_jsx(CnIcon, { type: iconType, size: 'small' })), name.trim() ? (_jsx("span", { className: 'cn-one-search-suggest-item-label', children: `${name.trim()}${isValueEmpty ? '' : '：'}` })) : null, isValueEmpty ? null : (_jsx("span", { className: 'cn-one-search-suggest-item-value', children: value }))] })) : (_jsx(SuggestCustomItemComp, { data: { ...item, name: value } })) }) }, key));
    };
    // 渲染搜索提示面板
    const renderContent = () => {
        return (_jsxs("div", { className: 'cn-one-search-suggest-view', children: [LeftSlotPluginComp ? (_jsx("div", { className: 'cn-one-search-left-plugin-container', children: _jsx(LeftSlotPluginComp, {}) })) : null, _jsx("div", { className: classnames('cn-one-search-suggest-view-content', {
                        'full-width': !LeftSlotPluginComp && !RightSlotPluginComp,
                    }), children: _jsxs(Menu, { selectMode: 'single', className: 'cn-one-search-suggest-menu', footer: _jsxs("div", { className: 'cn-one-search-suggest-footer', children: [_jsxs("div", { className: 'cn-one-search-suggest-footer-item', children: [_jsx("img", { src: 'https://img.alicdn.com/imgextra/i2/O1CN01EFyXXK1HyoNoD7vpX_!!6000000000827-55-tps-9-10.svg', style: { height: 10 } }), _jsx("span", { children: $i18n.get({ id: 'SwitchingConditions', dm: '切换条件' }) })] }), _jsxs("div", { className: 'cn-one-search-suggest-footer-item', children: [_jsx("img", { src: 'https://img.alicdn.com/imgextra/i4/O1CN01uAu24d1qyowVW0XsC_!!6000000005565-55-tps-10-9.svg', style: { height: 9 } }), _jsx("span", { children: $i18n.get({
                                                id: 'SelectExistingCondition',
                                                dm: '选择已有条件',
                                            }) })] }), _jsxs("div", { className: 'cn-one-search-suggest-footer-item', children: [_jsx("img", { src: 'https://img.alicdn.com/imgextra/i3/O1CN011EJC1N1OCRrvSdSYu_!!6000000001669-55-tps-10-5.svg', style: { height: 5 } }), _jsx("span", { children: $i18n.get({
                                                id: 'TriggerSearchTriggerEdit',
                                                dm: '触发搜索/触发编辑',
                                            }) })] }), _jsxs("div", { className: 'cn-one-search-suggest-footer-item', children: [_jsx("img", { src: 'https://img.alicdn.com/imgextra/i3/O1CN01TNDlz024ckTS1dK4D_!!6000000007412-55-tps-10-7.svg', style: { width: 10 } }), _jsx("span", { children: $i18n.get({ id: 'DeleteCondition', dm: '删除条件' }) })] })] }), children: [_jsx(Menu.Group, { label: SuggestHeadSlotPluginComp ? (_jsx(SuggestHeadSlotPluginComp, {})) : (_jsxs("div", { className: 'cn-one-search-suggest-header', onClick: () => {
                                        setIsSugRecommendFolder(!isSugRecommendFolder);
                                        setCurrentSuggestIndex(-1);
                                        setTimeout(() => {
                                            containerRef?.current?.querySelector('input')?.focus();
                                        });
                                    }, children: [_jsx(CnIcon, { className: 'cn-one-search-suggest-header-folder', type: isSugRecommendFolder
                                                ? 'triangle-right-fill'
                                                : 'triangle-down-fill', size: 'medium' }), _jsx("span", { children: $i18n.get({
                                                id: 'AIIntelligentRecommendationQueryCriteria',
                                                dm: 'AI智能推荐查询条件',
                                            }) }), _jsx(CnTooltip, { trigger: _jsx(CnIcon, { type: 'help-color' }), align: 't', children: $i18n.get({
                                                id: 'IntelligentRecommendationBasedOnInputContent_49292240',
                                                dm: '基于输入内容和历史查询记录智能推荐',
                                            }) })] })), children: isSugRecommendFolder
                                    ? []
                                    : suggestRecommend?.map(renderSuggestItems) }), suggestHistory?.length ? (_jsx(Menu.Group, { label: _jsxs("div", { className: 'cn-one-search-suggest-header', onClick: () => {
                                        setIsSugHisFolder(!isSugHisFolder);
                                        setCurrentSuggestIndex(-1);
                                        setTimeout(() => {
                                            containerRef?.current?.querySelector('input')?.focus();
                                        });
                                    }, children: [_jsx(CnIcon, { className: 'cn-one-search-suggest-header-folder', type: isSugHisFolder
                                                ? 'triangle-right-fill'
                                                : 'triangle-down-fill', size: 'medium' }), _jsx("span", { children: $i18n.get({ id: 'SearchHistory', dm: '搜索历史' }) }), _jsx(CnTooltip, { trigger: _jsx(CnIcon, { type: 'help-color' }), align: 't', children: $i18n.get({
                                                id: 'HistorySearchRecord',
                                                dm: '历史搜索记录',
                                            }) })] }), children: isSugHisFolder
                                    ? []
                                    : suggestHistory.map((item, index) => renderSuggestItems(item, index + suggestRecommend?.length)) })) : null] }) }), RightSlotPluginComp ? (_jsx("div", { className: 'right-plugin-container', children: _jsx(RightSlotPluginComp, {}) })) : null] }));
    };
    /** 外部调用Search的查询按钮能力 */
    useEffect(() => {
        const handleSearch = () => {
            searchRef.current?._instance?.onSearch?.();
        };
        event.on('cnOneSearchDoQuery', handleSearch);
        return () => {
            event.off('cnOneSearchDoQuery', handleSearch);
        };
    }, []);
    useEffect(() => {
        setNeedExpendShowSearchTags(isAutoExpand);
    }, [isAutoExpand]);
    return (_jsx(Search, { ref: (r) => {
            searchRef.current = r;
        }, disabled: disabled, 
        // @ts-ignore 这个属性的类型定义有问题
        innerBefore: _jsxs(_Fragment, { children: [_jsxs("span", { onClick: (e) => {
                        e.stopPropagation();
                    }, className: 'search-input-label', children: [customFilterSlot ? (_jsx("span", { className: 'custom-filter-slot', children: customFilterSlot })) : null, _jsx(QuickFieldButton, { quickSearchFields: quickSearchFields, searchTags: searchTags, onChange: (newTags) => {
                                onChangeSearchTags(newTags);
                                if (newTags.length > realShowLimitNum) {
                                    setNeedExpendShowSearchTags(true);
                                }
                            }, quickSearchButtonIcon: quickSearchButtonIcon, disabled: disabled }), _jsx(SearchTag, { tags: searchTags, onChange: onChangeSearchTags, setNeedExpendShowSearchTags: setNeedExpendShowSearchTags, needExpendShowSearchTags: needExpendShowSearchTags, selectedIndex: currentTagIndex, onTagClose: handleTagClose, onTagClick: handleTagClick, disabled: disabled, quickSearchFields: quickSearchFields, fieldRender: fieldRender, customShowLimitNum: customShowLimitNum })] }), _jsx("span", { className: 'one-search-clear-btn', onClick: handleClearClick, children: _jsx(CnIcon, { type: 'error-r-fill', size: 'small' }) })] }), placeholder: searchTags.length > 0 ? ' ' : placeholder, dataSource: suggest.map((item) => {
            return { ...item, value: `${item.value}_ts_${Math.random() * 100000}` };
        }), 
        // hasClear
        onChange: (k, actionType, item) => {
            const isFromEnterSelectItem = actionType === 'enter' && item && (item.value || item.name);
            if (isFromEnterSelectItem) {
                const newItem = {
                    ...item,
                    value: item.value?.split('_ts_')[0] || item.value,
                    label: item.label?.split('_ts_')[0] || item.label,
                };
                handleSuggestItemSelected(newItem);
                isOnSearchTriggerFromEnterSelectSearchItem = true;
            }
            else {
                onChangeSearchWord(k);
                setIsSearching(false);
                if (!isComposition) {
                    handleKeywordChange(k);
                }
            }
        }, onSearch: (searchValue) => {
            if (!isOnSearchTriggerFromEnterSelectSearchItem &&
                currentTagIndex === -1 &&
                (currentSuggestIndex === -1 || !suggestVisible)) {
                if (searchValue.indexOf('_ts_') > -1) {
                    handleKeywordSearch(searchValue?.split('_ts_')[0] || searchValue);
                }
                else {
                    handleKeywordSearch(searchValue);
                }
            }
        }, 
        // @ts-ignore 这个属性的类型定义有问题
        autoHighlightFirstItem: false, 
        // 已经在搜索中了，就不展示suggest了
        visible: suggestVisible && !isSearchingRef.current, onVisibleChange: (canShow) => {
            if (!canShow) {
                setSuggestVisible(false);
            }
        }, value: keyword, className: classnames({
            'cn-one-search-search-input': true,
            'search-input-expend-tags': needExpendShowSearchTags && searchTags.length > realShowLimitNum,
        }), type: 'secondary', 
        // 输入框焦点切换时重置tag选中态
        onFocus: () => {
            handleKeywordChange(keyword);
            setCurrentTagIndex(-1);
            setIsSearching(false);
            onFocus && onFocus();
        }, onBlur: () => {
            setCurrentTagIndex(-1);
            // setSuggestVisible(false);
        }, popupContent: renderContent(), filterLocal: false, state: loadingSuggest ? 'loading' : undefined, onKeyDown: handleKeyDownEvent, 
        // 增加对输入法输入中状态的检测，避免选词使用空格键跟变tag的操作冲突，并且减少输入过程中去请求搜索建议
        onCompositionStart: () => {
            setIsComposition(true);
        }, onCompositionEnd: (e) => {
            setIsComposition(false);
            if (e.target?.value) {
                handleKeywordChange(e.target.value);
            }
        }, ...suggestPopupProps, buttonProps: {
            loading,
        } }));
}
SearchInput.displayName = 'OneSearch.SearchInput';
export default SearchInput;
